(() => {
  if (window.customElements && !customElements.get('fexx-shoppable-stories')) {
    class FexxShoppableStories extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.track = this.querySelector('[data-fs-track]');
        this.cards = Array.from(this.querySelectorAll('[data-fs-card]'));
        this.modal = this.querySelector('[data-fs-modal]');
        this.modalDialog = this.querySelector('[data-fs-modal-dialog]');
        this.modalMedia = this.querySelector('[data-fs-modal-media]');
        this.modalTimeline = this.querySelector('[data-fs-modal-timeline]');
        this.productPanel = this.querySelector('[data-fs-product-panel]');
        this.productImage = this.querySelector('[data-fs-product-image]');
        this.productTitle = this.querySelector('[data-fs-product-title]');
        this.productPrice = this.querySelector('[data-fs-product-price]');
        this.productCompare = this.querySelector('[data-fs-product-compare]');
        this.productLink = this.querySelector('[data-fs-product-link]');
        this.soundOn = this.querySelector('[data-fs-sound-on]');
        this.soundOff = this.querySelector('[data-fs-sound-off]');
        this.likeButton = this.querySelector('[data-fs-like]');
        this.modalLikeCount = this.querySelector('[data-fs-like-count]');

        if (!this.track || !this.cards.length || !this.modal) return;

        this.autoplay = this.dataset.autoplay === 'true';
        this.pauseOnHover = this.dataset.pauseHover === 'true';
        this.previewMs = Math.max(Number(this.dataset.previewMs) || 3000, 1000);
        this.productPageStories = this.dataset.source === 'product_block';
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.activeIndex = 0;
        this.modalIndex = 0;
        this.previewTimer = null;
        this.scrollTimer = null;
        this.isVisible = true;
        this.isHovered = false;
        this.modalVideo = null;
        this.lastFocused = null;
        this.dragged = false;
        this.pointerDown = false;
        this.pointerCaptured = false;
        this.dragStartX = 0;
        this.dragStartScroll = 0;
        this.modalSwipeX = null;
        this.memoryLikes = new Map();
        this.numberFormatter = new Intl.NumberFormat('pt-BR', {
          notation: 'compact',
          maximumFractionDigits: 1
        });

        this.bindEvents();
        this.observeVisibility();
        this.cards.forEach((card) => this.updateLikeUI(card, false));
        this.activateCard(0, false);
      }

      disconnectedCallback() {
        this.clearPreviewTimer();
        if (this.observer) this.observer.disconnect();
      }

      bindEvents() {
        this.cards.forEach((card, index) => {
          card.addEventListener('click', (event) => {
            if (this.dragged) {
              event.preventDefault();
              this.dragged = false;
              return;
            }
            this.openModal(index);
          });
        });

        this.track.addEventListener('scroll', () => {
          window.clearTimeout(this.scrollTimer);
          this.scrollTimer = window.setTimeout(() => {
            const index = this.closestCardIndex();
            if (index !== this.activeIndex) this.activateCard(index, false);
          }, 120);
        }, { passive: true });

        this.track.addEventListener('pointerdown', (event) => {
          this.clearPreviewTimer();
          if (event.pointerType !== 'mouse') return;
          this.pointerDown = true;
          this.dragged = false;
          this.dragStartX = event.clientX;
          this.dragStartScroll = this.track.scrollLeft;
          this.track.classList.add('is-dragging');
        });

        this.track.addEventListener('pointermove', (event) => {
          if (!this.pointerDown) return;
          const delta = event.clientX - this.dragStartX;
          if (Math.abs(delta) > 4) {
            this.dragged = true;
            if (!this.pointerCaptured) {
              this.track.setPointerCapture?.(event.pointerId);
              this.pointerCaptured = true;
            }
          }
          this.track.scrollLeft = this.dragStartScroll - delta;
        });

        const finishDrag = (event) => {
          if (!this.pointerDown) return;
          this.pointerDown = false;
          this.track.classList.remove('is-dragging');
          if (this.pointerCaptured) {
            try { this.track.releasePointerCapture?.(event.pointerId); } catch (_) {}
          }
          this.pointerCaptured = false;
          this.activateCard(this.closestCardIndex(), false);
        };
        this.track.addEventListener('pointerup', finishDrag);
        this.track.addEventListener('pointercancel', finishDrag);
        this.track.addEventListener('pointerleave', (event) => {
          if (this.pointerDown) finishDrag(event);
        });

        if (this.pauseOnHover) {
          this.addEventListener('mouseenter', () => { this.isHovered = true; this.clearPreviewTimer(); });
          this.addEventListener('mouseleave', () => { this.isHovered = false; this.scheduleNext(); });
        }

        this.querySelectorAll('[data-fs-close]').forEach((button) => button.addEventListener('click', () => this.closeModal()));
        this.querySelector('[data-fs-prev]')?.addEventListener('click', () => this.showModalItem(this.modalIndex - 1));
        this.querySelector('[data-fs-next]')?.addEventListener('click', () => this.showModalItem(this.modalIndex + 1));
        this.querySelector('[data-fs-sound]')?.addEventListener('click', () => this.toggleSound());
        this.querySelector('[data-fs-share]')?.addEventListener('click', () => this.shareCurrent());
        this.querySelector('[data-fs-whatsapp]')?.addEventListener('click', () => this.shareWhatsApp());
        this.likeButton?.addEventListener('click', () => this.toggleLike());

        this.modalDialog?.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse') return;
          this.modalSwipeX = event.clientX;
        });
        this.modalDialog?.addEventListener('pointerup', (event) => {
          if (this.modalSwipeX === null) return;
          const delta = event.clientX - this.modalSwipeX;
          this.modalSwipeX = null;
          if (Math.abs(delta) < 45) return;
          this.showModalItem(delta > 0 ? this.modalIndex - 1 : this.modalIndex + 1);
        });

        document.addEventListener('keydown', (event) => {
          if (this.modal.hidden) return;
          if (event.key === 'Escape') this.closeModal();
          if (event.key === 'ArrowLeft') this.showModalItem(this.modalIndex - 1);
          if (event.key === 'ArrowRight') this.showModalItem(this.modalIndex + 1);
        });

        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            this.clearPreviewTimer();
            this.pauseCardVideos();
          } else {
            this.activateCard(this.activeIndex, false);
          }
        });

        document.addEventListener('shopify:block:select', (event) => {
          const card = event.target?.closest?.('[data-fs-card]');
          if (!card || !this.contains(card)) return;
          const index = this.cards.indexOf(card);
          if (index >= 0) this.activateCard(index, true);
        });
      }

      observeVisibility() {
        if (!('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          this.isVisible = Boolean(entry?.isIntersecting);
          if (this.isVisible) this.activateCard(this.activeIndex, false);
          else {
            this.clearPreviewTimer();
            this.pauseCardVideos();
          }
        }, { threshold: 0.15 });
        this.observer.observe(this);
      }

      cardVideo(card) { return card?.querySelector('video'); }
      clearPreviewTimer() { if (this.previewTimer) window.clearTimeout(this.previewTimer); this.previewTimer = null; }
      pauseCardVideos() { this.cards.forEach((card) => { const video = this.cardVideo(card); if (video) video.pause(); }); }

      playCardVideo(card) {
        const video = this.cardVideo(card);
        if (!video) return;
        video.muted = true;

        if (this.productPageStories) {
          video.loop = false;
          const clipSeconds = this.previewMs / 1000;

          if (video.dataset.fsClipLoopBound !== 'true') {
            video.dataset.fsClipLoopBound = 'true';

            const restartPreview = () => {
              video.currentTime = 0;
              if (this.isVisible && !document.hidden && this.modal.hidden) {
                const restartPromise = video.play();
                if (restartPromise?.catch) restartPromise.catch(() => {});
              }
            };

            video.addEventListener('timeupdate', () => {
              if (video.currentTime >= clipSeconds) restartPreview();
            });
            video.addEventListener('ended', restartPreview);
          }

          if (video.currentTime >= clipSeconds) video.currentTime = 0;
        } else {
          video.loop = true;
        }

        const promise = video.play();
        if (promise?.catch) promise.catch(() => {});
      }

      activateCard(index, shouldScroll = true) {
        if (!this.cards.length) return;
        this.activeIndex = (index + this.cards.length) % this.cards.length;
        this.clearPreviewTimer();
        this.cards.forEach((card, cardIndex) => {
          card.classList.toggle('is-active', cardIndex === this.activeIndex);
          const video = this.cardVideo(card);
          if (video && cardIndex !== this.activeIndex && !this.productPageStories) video.pause();
        });
        const activeCard = this.cards[this.activeIndex];
        if (shouldScroll) activeCard.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
        if (this.isVisible && !document.hidden) {
          if (this.productPageStories) this.cards.forEach((card) => this.playCardVideo(card));
          else this.playCardVideo(activeCard);
        }
        this.scheduleNext();
      }

      scheduleNext() {
        this.clearPreviewTimer();
        if (this.productPageStories) return;
        if (!this.autoplay || this.reduceMotion || !this.isVisible || document.hidden || this.isHovered || !this.modal.hidden || this.cards.length < 2) return;
        this.previewTimer = window.setTimeout(() => this.activateCard(this.activeIndex + 1, true), this.previewMs);
      }

      closestCardIndex() {
        const center = this.track.scrollLeft + (this.track.clientWidth / 2);
        let nearest = 0;
        let distance = Infinity;
        this.cards.forEach((card, index) => {
          const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
          const current = Math.abs(cardCenter - center);
          if (current < distance) { distance = current; nearest = index; }
        });
        return nearest;
      }

      openModal(index) {
        this.lastFocused = document.activeElement;
        this.clearPreviewTimer();
        this.pauseCardVideos();
        this.modal.hidden = false;
        document.body.classList.add('fexx-stories-modal-open');
        this.showModalItem(index);
        window.requestAnimationFrame(() => this.modalDialog?.focus());
      }

      closeModal() {
        if (this.modal.hidden) return;
        if (this.modalVideo) {
          this.modalVideo.pause();
          this.modalVideo.remove();
          this.modalVideo = null;
        }
        this.modal.hidden = true;
        document.body.classList.remove('fexx-stories-modal-open');
        this.lastFocused?.focus?.();
        this.activateCard(this.activeIndex, false);
      }

      showModalItem(index) {
        if (!this.cards.length) return;
        this.modalIndex = (index + this.cards.length) % this.cards.length;
        const card = this.cards[this.modalIndex];
        const sourceVideo = this.cardVideo(card);

        if (this.modalVideo) {
          this.modalVideo.pause();
          this.modalVideo.remove();
        }
        this.modalMedia.innerHTML = '';

        if (sourceVideo) {
          const video = sourceVideo.cloneNode(true);
          video.className = 'fexx-stories-modal__video';
          video.removeAttribute('loop');
          video.loop = false;
          video.muted = false;
          video.autoplay = true;
          video.controls = false;
          video.playsInline = true;
          video.preload = 'auto';
          video.currentTime = 0;
          video.addEventListener('timeupdate', () => {
            const progress = video.duration ? video.currentTime / video.duration : 0;
            if (this.modalTimeline) this.modalTimeline.style.transform = `scaleX(${Math.max(0, Math.min(progress, 1))})`;
          });
          video.addEventListener('ended', () => this.showModalItem(this.modalIndex + 1));
          this.modalMedia.appendChild(video);
          this.modalVideo = video;
          const promise = video.play();
          if (promise?.catch) promise.catch(() => {
            video.muted = true;
            video.play().catch(() => {});
            this.updateSoundState();
          });
        }

        this.updateProduct(card);
        this.updateLikeUI(card, true);
        this.updateSoundState();
      }

      updateProduct(card) {
        const title = card.dataset.productTitle || '';
        const price = card.dataset.productPrice || '';
        const compare = card.dataset.productCompare || '';
        const image = card.dataset.productImage || '';
        const url = card.dataset.productUrl || '';
        const hasProduct = Boolean(title || url);

        if (this.productPanel) this.productPanel.hidden = !hasProduct;
        if (this.productTitle) this.productTitle.textContent = title;
        if (this.productPrice) this.productPrice.textContent = price;
        if (this.productCompare) {
          this.productCompare.textContent = compare;
          this.productCompare.hidden = !compare;
        }
        if (this.productImage) {
          this.productImage.src = image || '';
          this.productImage.alt = title;
          this.productImage.hidden = !image;
        }
        if (this.productLink) {
          this.productLink.href = url || '#';
          this.productLink.hidden = !url;
        }
      }

      likeStorageKey(card) {
        const key = card?.dataset.likeKey || `${window.location.pathname}-${card?.dataset.index || 0}`;
        return `fexx-story-liked:${key}`;
      }

      isLiked(card) {
        const key = this.likeStorageKey(card);
        if (this.memoryLikes.has(key)) return this.memoryLikes.get(key);
        try {
          const liked = window.localStorage.getItem(key) === '1';
          this.memoryLikes.set(key, liked);
          return liked;
        } catch (_) {
          return false;
        }
      }

      setLiked(card, liked) {
        const key = this.likeStorageKey(card);
        this.memoryLikes.set(key, liked);
        try {
          if (liked) window.localStorage.setItem(key, '1');
          else window.localStorage.removeItem(key);
        } catch (_) {}
      }

      likeTotal(card) {
        const base = Math.max(0, Number.parseInt(card?.dataset.likeBase || '0', 10) || 0);
        return base + (this.isLiked(card) ? 1 : 0);
      }

      formatLikes(value) {
        const safeValue = Math.max(0, Number(value) || 0);
        return this.numberFormatter.format(safeValue);
      }

      updateLikeUI(card, updateModal = true) {
        if (!card) return;
        const showLikes = card.dataset.showLikes === 'true';
        const liked = showLikes && this.isLiked(card);
        const total = this.likeTotal(card);
        const cardCount = card.querySelector('[data-fs-card-like-count]');

        card.classList.toggle('is-liked', liked);
        if (cardCount) cardCount.textContent = this.formatLikes(total);

        if (!updateModal || !this.likeButton) return;
        this.likeButton.hidden = !showLikes;
        this.likeButton.classList.toggle('is-liked', liked);
        this.likeButton.setAttribute('aria-pressed', liked ? 'true' : 'false');
        this.likeButton.setAttribute('aria-label', liked ? 'Remover curtida' : 'Curtir vídeo');
        if (this.modalLikeCount) this.modalLikeCount.textContent = this.formatLikes(total);
      }

      toggleLike() {
        const card = this.currentCard();
        if (!card || card.dataset.showLikes !== 'true') return;
        this.setLiked(card, !this.isLiked(card));
        this.updateLikeUI(card, true);
      }

      toggleSound() {
        if (!this.modalVideo) return;
        this.modalVideo.muted = !this.modalVideo.muted;
        if (this.modalVideo.paused) this.modalVideo.play().catch(() => {});
        this.updateSoundState();
      }

      updateSoundState() {
        const muted = !this.modalVideo || this.modalVideo.muted;
        if (this.soundOn) this.soundOn.hidden = muted;
        if (this.soundOff) this.soundOff.hidden = !muted;
      }

      currentCard() { return this.cards[this.modalIndex] || this.cards[0]; }
      currentUrl() {
        const card = this.currentCard();
        try { return card?.dataset.productUrl ? new URL(card.dataset.productUrl, window.location.origin).href : window.location.href; }
        catch (_) { return window.location.href; }
      }

      async shareCurrent() {
        const card = this.currentCard();
        const data = { title: card?.dataset.productTitle || document.title, url: this.currentUrl() };
        if (navigator.share) {
          try { await navigator.share(data); return; } catch (_) {}
        }
        try { await navigator.clipboard.writeText(data.url); } catch (_) {}
      }

      shareWhatsApp() {
        const message = this.dataset.whatsappMessage || 'Olha este produto:';
        window.open(`https://wa.me/?text=${encodeURIComponent(`${message} ${this.currentUrl()}`)}`, '_blank', 'noopener');
      }
    }

    customElements.define('fexx-shoppable-stories', FexxShoppableStories);
  }
})();
